import React, { useEffect, useState } from 'react';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';

import { UiValidIconIndicator } from './ui-valid-icon-indicator';

type InputProps = {
  // Our configurations
  onError?: (error: string | null) => void;
  customAdornment?: JSX.Element;
  errorText: string | null;
  isValid: boolean | null;
  // Configurations that are on both, but that we override
  value: string | null;
  onChange: (phoneNumber: string | null) => void;

  // Added by React
  innerRef?: any;
};

type Props = InputProps & Omit<TextFieldProps, keyof InputProps>;

export const UiAreaCodeTextField = ({
  label,
  required,
  value,
  onChange,
  onError,
  customAdornment,
  isValid,
  errorText,
  helperText,
  ...textFieldProps
}: Props) => {
  const [areaCodeError, setAreaCodeError] = useState<string | null>(null);
  const [isAreaCodeValid, setIsAreaCodeValid] = useState<boolean | null>(null);
  useEffect(() => {
    setIsAreaCodeValid(isValid);
  }, [isValid]);

  const onBlurHandler = () => {
    if (!required && !value) {
      setAreaCodeError(null);
      setIsAreaCodeValid(null);
      if (onError) {
        onError(null);
      }
    }

    if (required && (!value || value.length < 3)) {
      setAreaCodeError('Area code must be 3 digits');
      setIsAreaCodeValid(false);
    }
  };

  const areaCodeOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAreaCode = e.target.value;

    const numbersInValue = newAreaCode.replace(/[^\d]/g, '');

    if (numbersInValue.length > 3) {
      setAreaCodeError('Area code must be 3 digits');
      setIsAreaCodeValid(false);
    } else {
      setAreaCodeError(null);
      setIsAreaCodeValid(null);
    }
    onChange(numbersInValue);
  };

  const inputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <UiValidIconIndicator isValid={isAreaCodeValid} />
      </InputAdornment>
    ),
  };

  return (
    <TextField
      value={value}
      onChange={areaCodeOnChangeHandler}
      onBlur={onBlurHandler}
      error={Boolean(areaCodeError) || isAreaCodeValid === false}
      helperText={areaCodeError || errorText || helperText}
      // Passthrough options
      label={label ?? 'Area Code'}
      InputProps={inputProps}
      required={required}
      {...textFieldProps}
    />
  );
};
